import Flash from '@/components/flash';
import CaprockMark from '@/components/linear/caprock-mark';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  admin_accounts_path,
  admin_bot_actions_path,
  admin_claims_path,
  admin_draft_exceptions_path,
  admin_provisionings_path,
  admin_wiring_path,
  app_attachments_path,
  app_connections_path,
  app_customers_path,
  app_design_path,
  app_developer_path,
  app_drafts_path,
  app_invoices_path,
  app_list_records_path,
  app_messages_path,
  app_path,
  app_publication_path,
  apps_path,
  billing_path,
  console_company_path,
  console_login_path,
  inbox_path,
  session_path,
} from '@/rails/routes';
import type { Breadcrumb } from '@/types/breadcrumb';
import type { AppShell, ConsoleShared } from '@/types/platform';
import type { SharedCompany } from '@/types/shared';
import type { CurrentUser } from '@/types/user';
import { Head, Link, router, usePage } from '@inertiajs/react';
import { ChevronRight, Menu } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

interface ConsoleLayoutProps {
  children: ReactNode;
  flash: { notice?: string; error?: string };
  pageTitle?: string;
}

interface ConsolePageProps {
  pageTitle?: string;
  currentPath?: string;
  breadcrumbs?: Breadcrumb[];
  companies?: SharedCompany[];
  currentCompany?: SharedCompany | null;
  currentUser?: CurrentUser | null;
  staff?: boolean;
  console?: ConsoleShared | null;
  app?: AppShell;
  [key: string]: unknown;
}

interface NavItem {
  href: string;
  label: string;
  // Matches the item itself and anything nested under it, unless exact is set.
  exact?: boolean;
}

const MAIN_CONTENT_ID = 'main-content';

export default function ConsoleLayout({ children, flash, pageTitle }: ConsoleLayoutProps) {
  const { props, url } = usePage<ConsolePageProps>();
  const title = pageTitle || props.pageTitle || 'Console';
  const path = props.currentPath || url.split('?')[0];
  const [menuOpen, setMenuOpen] = useState(false);

  // The console is a light surface. A dark preference left by the workspace must not leak in.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.removeAttribute('data-linear-theme');
  }, []);

  const navigation = <Navigation props={props} path={path} onNavigate={() => setMenuOpen(false)} />;

  return (
    <>
      <Head title={`${title} · CAP Rock Labs`} />
      <TooltipProvider>
        <a
          href={`#${MAIN_CONTENT_ID}`}
          className="bg-background sr-only z-50 rounded px-3 py-2 text-sm focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
        >
          Skip to content
        </a>
        <div className="bg-muted/30 text-foreground flex min-h-screen">
          <aside className="bg-background hidden w-64 shrink-0 border-r lg:block">
            <div className="sticky top-0 flex max-h-screen flex-col overflow-y-auto">
              {navigation}
            </div>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="bg-background flex h-14 items-center gap-3 border-b px-4 lg:hidden">
              <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    aria-label="Open menu"
                    className="hover:bg-muted rounded-md p-2"
                  >
                    <Menu className="h-5 w-5" aria-hidden="true" />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 overflow-y-auto p-0">
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <SheetDescription className="sr-only">Console navigation</SheetDescription>
                  {navigation}
                </SheetContent>
              </Sheet>
              <span className="text-sm font-semibold">CAP Rock Labs</span>
            </header>

            <DemoStrip console={props.console} staff={props.staff ?? false} />

            <main id={MAIN_CONTENT_ID} className="min-w-0 flex-1 px-4 py-6 sm:px-8">
              <div className="mx-auto w-full max-w-6xl">
                <Breadcrumbs items={props.breadcrumbs ?? []} />
                {children}
              </div>
            </main>
          </div>
        </div>
        <Flash {...flash} />
        <Toaster />
      </TooltipProvider>
    </>
  );
}

function Navigation({
  props,
  path,
  onNavigate,
}: {
  props: ConsolePageProps;
  path: string;
  onNavigate: () => void;
}) {
  const app = props.app;
  const companies = props.companies ?? [];

  const accountItems: NavItem[] = [
    { href: apps_path(), label: 'Apps', exact: !app },
    { href: billing_path(), label: 'Plan and usage' },
  ];

  const staffItems: NavItem[] = [
    { href: admin_accounts_path(), label: 'Accounts' },
    { href: admin_draft_exceptions_path(), label: 'Exceptions' },
    { href: admin_provisionings_path(), label: 'Provisioning' },
    { href: admin_claims_path(), label: 'Handoffs' },
    { href: admin_wiring_path(), label: 'Wiring' },
    { href: admin_bot_actions_path(), label: 'Account bot' },
  ];

  return (
    <nav aria-label="Console" className="flex min-h-full flex-col gap-6 p-4">
      <Link href={apps_path()} onClick={onNavigate} className="flex items-center gap-2.5 px-2">
        <CaprockMark size={26} />
        <span className="text-sm font-semibold tracking-tight">CAP Rock Labs</span>
      </Link>

      <CompanySwitcher companies={companies} current={props.currentCompany ?? null} />

      <NavSection label={props.console?.plan ? `${props.console.plan.name} plan` : 'Account'}>
        {accountItems.map((item) => (
          <NavLink key={item.href} item={item} path={path} onNavigate={onNavigate} />
        ))}
      </NavSection>

      {app ? (
        <NavSection label={app.name}>
          {appItems(app).map((item) => (
            <NavLink key={item.href} item={item} path={path} onNavigate={onNavigate} />
          ))}
        </NavSection>
      ) : null}

      {props.staff ? (
        <NavSection label="Staff">
          {staffItems.map((item) => (
            <NavLink key={item.href} item={item} path={path} onNavigate={onNavigate} />
          ))}
          <a
            href={inbox_path()}
            className="text-muted-foreground hover:bg-muted hover:text-foreground block rounded-md px-2 py-1.5 text-sm"
          >
            Workspace
          </a>
        </NavSection>
      ) : null}

      <div className="mt-auto border-t pt-4">
        <p className="truncate px-2 text-sm font-medium">{props.currentUser?.name}</p>
        <p className="text-muted-foreground truncate px-2 text-xs">
          {props.currentUser?.emailAddress}
        </p>
        <Link
          href={console_login_path()}
          onClick={onNavigate}
          className="text-muted-foreground hover:bg-muted hover:text-foreground mt-2 block rounded-md px-2 py-1.5 text-sm"
        >
          Your login
        </Link>
        <button
          type="button"
          onClick={() => router.delete(session_path())}
          className="text-muted-foreground hover:bg-muted hover:text-foreground w-full rounded-md px-2 py-1.5 text-left text-sm"
        >
          Log out
        </button>
      </div>
    </nav>
  );
}

function appItems(app: AppShell): NavItem[] {
  const items: NavItem[] = [{ href: app_path(app.id), label: 'Overview', exact: true }];

  app.lists.forEach((list) => {
    items.push({ href: app_list_records_path(app.id, list.key), label: list.name });
  });

  items.push(
    { href: app_customers_path(app.id), label: 'Customers' },
    { href: app_invoices_path(app.id), label: 'Invoices' },
    { href: app_messages_path(app.id), label: 'Messages' },
    { href: app_attachments_path(app.id), label: 'Attachments' },
    { href: app_connections_path(app.id), label: 'Connections' },
    { href: app_design_path(app.id), label: 'Design' },
    { href: app_drafts_path(app.id), label: 'Design with AI' },
    { href: app_publication_path(app.id), label: 'Publish' }
  );

  if (app.features.developer !== 'none') {
    items.push({ href: app_developer_path(app.id), label: 'Developer' });
  }

  return items;
}

function NavSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground mb-1 truncate px-2 text-xs font-medium tracking-wide uppercase">
        {label}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavLink({
  item,
  path,
  onNavigate,
}: {
  item: NavItem;
  path: string;
  onNavigate: () => void;
}) {
  const active = item.exact
    ? path === item.href
    : path === item.href || path.startsWith(`${item.href}/`);

  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'block truncate rounded-md px-2 py-1.5 text-sm',
        active
          ? 'bg-muted text-foreground font-medium'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      {item.label}
    </Link>
  );
}

function CompanySwitcher({
  companies,
  current,
}: {
  companies: SharedCompany[];
  current: SharedCompany | null;
}) {
  if (companies.length < 2) {
    return current ? <p className="truncate px-2 text-sm font-medium">{current.name}</p> : null;
  }

  return (
    <label className="block px-2">
      <span className="sr-only">Company</span>
      <select
        className="border-input bg-background w-full rounded-md border px-2 py-1.5 text-sm"
        value={current?.id ?? ''}
        onChange={(event) =>
          router.patch(console_company_path(), { company_id: event.target.value })
        }
      >
        {companies.map((company) => (
          <option key={company.id} value={company.id}>
            {company.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// Says plainly which services are stand-ins, so nobody mistakes a demo for the real thing.
// Staff see which providers still need keys. A builder cannot act on that, so they get plain words.
function DemoStrip({ console: shared, staff }: { console?: ConsoleShared | null; staff: boolean }) {
  if (!shared?.demo || shared.demoProviders.length === 0) return null;

  return (
    <div className="border-b border-yellow-200 bg-yellow-50 px-4 py-2 text-xs text-yellow-900 sm:px-8">
      <span className="font-medium">Demo mode.</span>{' '}
      {staff
        ? `These are simulated until their keys are set: ${shared.demoProviders.join(', ')}. No charge, email, or outside change is made by them.`
        : 'Payments, email, and QuickBooks are simulated here. No charge is made and no email is sent.'}
    </div>
  );
}

function Breadcrumbs({ items }: { items: Breadcrumb[] }) {
  if (items.length < 2) return null;

  return (
    <nav aria-label="Breadcrumb" className="text-muted-foreground mb-4 text-sm">
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((item, index) => (
          <li key={`${item.text}-${index}`} className="flex items-center gap-1">
            {index > 0 ? <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /> : null}
            {item.href ? (
              <Link href={item.href} className="hover:text-foreground">
                {item.text}
              </Link>
            ) : (
              <span className="text-foreground">{item.text}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
